'use client';

import Link from 'next/link';
import { Menu, X, ChevronDown } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { Logo } from './logo';
import { Button } from '@/components/ui/button';
import { NAV_LINKS } from '@/lib/constants';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function Header() {
  const pathname = usePathname();

  // Priority+ Navigation split for Tablet
  const priorityLinks = NAV_LINKS.slice(0, 5);
  const otherLinks = NAV_LINKS.slice(5);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-all duration-300">
      <div className="container flex h-16 max-w-7xl items-center justify-between px-4">
        <Logo />
        
        {/* Desktop Navigation (>= 1280px) */}
        <nav className="hidden xl:flex items-center gap-6 text-sm font-semibold uppercase tracking-wider">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              id={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              href={link.href}
              className={cn(
                'relative py-2 transition-colors hover:text-primary',
                pathname === link.href ? 'text-primary after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full after:bg-primary' : 'text-muted-foreground'
              )}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Tablet Navigation (768px - 1279px) */}
        <nav className="hidden md:flex xl:hidden items-center gap-4 text-sm font-semibold uppercase tracking-wider">
          {priorityLinks.map((link) => (
            <Link
              key={link.href}
              id={`nav-tablet-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              href={link.href}
              className={cn(
                'transition-colors hover:text-primary whitespace-nowrap',
                pathname === link.href ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              {link.label}
            </Link>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-1 font-semibold uppercase tracking-wider text-muted-foreground hover:text-primary">
                Other Tabs <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-background/95 backdrop-blur-md">
              {otherLinks.map((link) => (
                <DropdownMenuItem key={link.href} asChild>
                  <Link
                    href={link.href}
                    id={`nav-tablet-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={cn(
                      "w-full cursor-pointer",
                      pathname === link.href ? "text-primary font-bold" : ""
                    )}
                  >
                    {link.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="flex items-center gap-4">
          <Button asChild variant="accent" id="nav-book-a-free-demo" className="hidden sm:flex rounded-full shadow-md hover:shadow-lg transition-all duration-300 font-bold px-6">
            <Link href="/book-demo">Book a Free Demo</Link>
          </Button>
          
          <Sheet>
            <SheetTrigger asChild id="mobile-menu-trigger">
              <Button variant="outline" size="icon" className="md:hidden border-2 hover:bg-secondary">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader className="sr-only">
                    <SheetTitle>Menu</SheetTitle>
                    <SheetDescription>Main navigation menu</SheetDescription>
                </SheetHeader>
              <div className="flex flex-col h-full">
                <div className="flex justify-between items-center border-b pb-4">
                    <Logo />
                    <SheetClose asChild>
                        <Button variant="ghost" size="icon">
                            <X className="h-6 w-6" />
                        </Button>
                    </SheetClose>
                </div>
                <nav className="flex flex-col gap-4 mt-8 text-lg font-medium">
                  {NAV_LINKS.map((link) => (
                    <SheetClose asChild key={link.href}>
                      <Link
                        href={link.href}
                        id={`nav-mobile-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          'transition-colors py-2 border-b border-border/10',
                          pathname === link.href ? 'text-primary font-bold pl-2 border-l-4 border-l-primary' : 'text-foreground hover:pl-2 transition-all'
                        )}
                      >
                        {link.label}
                      </Link>
                    </SheetClose>
                  ))}
                </nav>
                <div className="mt-auto pt-6">
                    <Button asChild variant="accent" id="nav-mobile-book-a-free-demo" className="w-full rounded-full" size="lg">
                        <Link href="/book-demo">Book a Free Demo</Link>
                    </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
